import KioskLoading from '@/components/common/kiosk-loading';
import { WPFMessageType } from '@/enums';
import { BuildingNameType } from '@/enums/building';
import { useOrientation } from '@/hooks';
import { MainLayout } from '@/layouts';
import { NavigationProvider } from '@/providers/NavigationProvider';
import { PATHS } from '@/router/paths';
import { TrackingService } from '@/services/analytics/TrackingService';
import { cleanupConfigStore, runFromStep, useConfigStore } from '@/stores';
import { AppUtils } from '@/utils/app';
import { syncNavigationHistory } from '@/utils/navigationHistory';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';
import { ErrorBoundary } from './components/common/error-boundary';
import { ScreenSizeProvider } from './contexts/providers/ScreenSizeProvider';
import { useFocusVisible } from './hooks/useFocusVisible';
import { InitKiosk } from './layouts/init-kiosk/initKiosk';
import { useGetSvgDataQuery } from './services/queries/svg';

const App = () => {
    const { t, i18n } = useTranslation();
    const { pathname } = useLocation();
    const { isHorizontal } = useOrientation();

    // 👉 chỉ lấy state cần thiết để tránh re-render toàn bộ khi config thay đổi
    const isOnline = useConfigStore((s) => s.isOnline);
    const initConfig = useConfigStore((s) => s.initConfig);
    const updateConfig = useConfigStore((s) => s.updateConfig);
    const contrast = useConfigStore((s) => s.config?.ui?.contrast);
    const screenMode = useConfigStore((s) => s.config?.ui?.screenMode);
    const screenSaver = useConfigStore((s) => s.config?.ui?.screenSaver);
    const ui = useConfigStore((s) => s.config?.ui);
    const language = useConfigStore((s) => s.config?.ui?.language);
    const isLoading = useConfigStore((s) => s.isLoading);
    const setLoading = useConfigStore((s) => s.setLoading);
    const step = useConfigStore((s) => s.step);
    const setStep = useConfigStore((s) => s.setStep);
    const buildingType = useConfigStore((s) => s?.config?.server?.building?.buildingType);
    const voice = useConfigStore((state) => state.config.tts.voice);
    const zoomIn = useConfigStore((state) => state.config?.ui.zoomIn);
    const theme = useConfigStore((state) => state.config?.server?.store?.companyTheme);

    const kioskCode = localStorage.getItem('kioskCode');

    const pendingUpdatesRef = useRef<Map<string, { id: string; path: string }>>(new Map());
    const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const { isFetching: isFetchingSvgData } = useGetSvgDataQuery(Array.isArray(ui?.menu) ? ui.menu : []);

    // batch update screensaver (không spread config.ui)
    const batchUpdateScreenSaver = useCallback(async () => {
        if (pendingUpdatesRef.current.size === 0) return;
        try {
            const updates = Array.from(pendingUpdatesRef.current.values());
            const updatedScreens = screenSaver.map((item) => {
                const update = updates.find((u) => u.id === item.id);
                return update ? { ...item, localPath: update.path, downloadDate: new Date().toISOString() } : item;
            });

            updateConfig({
                ui: {
                    ...ui,
                    screenSaver: updatedScreens,
                },
            });

            pendingUpdatesRef.current.clear();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error in batch update:', error);
        } finally {
            setLoading(false);
        }
    }, [screenSaver, setLoading, ui, updateConfig]);

    const handleDownloadComplete = useCallback(
        async (message: { type: WPFMessageType; items: Array<{ id: string; path: string }> }) => {
            setLoading(true);
            runFromStep((state: { step?: number; isLoading?: boolean }) => {
                if (state.step !== undefined) {
                    setStep(state.step);
                }
                if (state.isLoading !== undefined) {
                    setLoading(state.isLoading);
                }
            }, 3);

            if (message.items && Array.isArray(message.items)) {
                message.items.forEach((item: { id: string; path: string }) => {
                    pendingUpdatesRef.current.set(item.id, {
                        id: item.id,
                        path: item.path,
                    });
                });
            }

            // Clear any existing timeout
            if (updateTimeoutRef.current) {
                clearTimeout(updateTimeoutRef.current);
            }

            // Update all items at once
            await batchUpdateScreenSaver();
        },
        [setLoading, setStep, batchUpdateScreenSaver]
    );

    useEffect(() => {
        const handler = async (event: MessageEvent) => {
            const message = event.data;

            switch (message.type) {
                case WPFMessageType.BatchImageDownloadComplete:
                    handleDownloadComplete(message);
                    break;

                default:
                    break;
            }
        };

        const updateTimeout = updateTimeoutRef.current;

        window.chrome?.webview?.addEventListener('message', handler);
        return () => {
            window.chrome?.webview?.removeEventListener('message', handler);
            if (updateTimeout) {
                clearTimeout(updateTimeout);
            }
        };
    }, [handleDownloadComplete]);

    // init config
    useEffect(() => {
        initConfig();

        return () => {
            cleanupConfigStore();
        };
    }, [initConfig]);

    // update CSS variables for orientation
    useEffect(() => {
        document.documentElement.style.setProperty('--BASE-WIDTH', isHorizontal ? '1920' : '1080');
        document.documentElement.style.setProperty('--BASE-HEIGHT', isHorizontal ? '1080' : '1920');
    }, [isHorizontal]);

    // Sync language from config store with i18n
    useEffect(() => {
        if (language && language !== i18n.language) {
            i18n.changeLanguage(language);
        }
    }, [language, i18n]);

    // Update font based on language
    useEffect(() => {
        if (language) {
            const fontFamily = AppUtils.getLanguageFont(language, buildingType);
            document.documentElement.style.setProperty('--font-text', fontFamily);
            if (language === 'en' && buildingType === BuildingNameType.MALL) {
                document.querySelector('body')?.classList.add('font-english-mall');
            } else {
                document.querySelector('body')?.classList.remove('font-english-mall');
            }
        }
    }, [language, buildingType]);

    // lang & pathname
    useEffect(() => {
        document.body.setAttribute('lang', i18n.language);
        window.scrollTo(0, 0);
    }, [i18n.language, pathname]);

    // route change wiring for tracking (detect leave/enter Home base)
    const prevPathRef = useRef<string>(PATHS.HOME.INDEX);
    useEffect(() => {
        const prev = prevPathRef.current;
        const next = pathname;
        TrackingService.onRouteChange(prev, next);
        syncNavigationHistory(next);
        prevPathRef.current = next;
    }, [pathname]);

    // contrast class + company theme (same CSS vars, different values)
    useEffect(() => {
        const root = document.documentElement;
        const body = document.body;

        if (contrast) {
            body.setAttribute('data-theme', 'contrast');
            body.classList.add('contrast');
        } else {
            body.removeAttribute('data-theme');
            body.classList.remove('contrast');
        }

        root.style.setProperty('--bg-primary', contrast ? theme?.menu_button_bg_color_hc || '#4F74F9' : theme?.menu_button_bg_color || '#64BEF0');
        root.style.setProperty('--text-primary', contrast ? theme?.menu_button_font_color_hc || '#FFFFFF' : theme?.menu_button_font_color || '#222222');
        root.style.setProperty('--bg-secondary', contrast ? theme?.sub_button_bg_color_hc || '#272A36' : theme?.sub_button_bg_color || '#FFFFFF');
        root.style.setProperty('--text-secondary', contrast ? theme?.sub_button_font_color_hc || '#FFFFFF' : theme?.sub_button_font_color || '#FFF');
        root.style.setProperty('--bg-router', contrast ? theme?.route_line_color_hc || '#2CC4CE' : theme?.route_line_color || '#2CC4CE');
        root.style.setProperty('--bg-marker', contrast ? theme?.route_marker_color_hc || '#1B53E3' : theme?.route_marker_color || '#FFFFFF');
        root.style.setProperty('--bg-footer', contrast ? theme?.footer_bg_color_hc || '#242424' : theme?.footer_bg_color || '#171A24');
        root.style.setProperty('--text-footer', contrast ? theme?.footer_font_color_hc || '#CCCCCC' : theme?.footer_font_color || '#CCCCCC');
    }, [contrast, theme]);

    useEffect(() => {
        document.body.classList.toggle('mall-branch', buildingType === BuildingNameType.MALL);
    }, [buildingType]);

    // screen mode
    useEffect(() => {
        if (screenMode === 'fullscreen') {
            document.body.setAttribute('data-screen-mode', 'fullscreen');
        } else if (screenMode === 'split') {
            document.body.setAttribute('data-screen-mode', 'split');
        } else {
            document.body.removeAttribute('data-screen-mode');
        }
        document.body.classList.toggle('fullscreen', screenMode === 'fullscreen');
    }, [screenMode]);

    useEffect(() => {
        document.body.setAttribute('data-orientation', isHorizontal ? 'horizontal' : 'vertical');
        document.body.classList.toggle('widescreen', isHorizontal);
    }, [isHorizontal]);

    // Focus visible - detect keyboard vs mouse/touch interaction
    useEffect(() => {
        document.body.setAttribute('data-focus-visible', !voice ? 'true' : 'false');
    }, [voice]);

    useFocusVisible();

    useEffect(() => {
        if (zoomIn) {
            document.body.classList.remove('overflow-hidden');
        } else {
            document.body.classList.add('overflow-hidden');
        }
    }, [zoomIn]);

    return (
        <ErrorBoundary>
            <ScreenSizeProvider>
                <NavigationProvider>
                    <KioskLoading
                        show={!isOnline || (isLoading && !!kioskCode) || isFetchingSvgData}
                        content={
                            <div>
                                {!isOnline ? (
                                    <p dangerouslySetInnerHTML={{ __html: t('loading.network_error') }} />
                                ) : (
                                    <>
                                        <p dangerouslySetInnerHTML={{ __html: t('loading.step') }} />
                                        <p className="mt-2.5 font-bold">{step} / 3</p>
                                    </>
                                )}
                            </div>
                        }
                        footerShow={!isOnline}
                    />
                    {isOnline &&
                        !isLoading &&
                        (kioskCode ? (
                            <MainLayout>
                                <Outlet />
                            </MainLayout>
                        ) : (
                            <InitKiosk />
                        ))}
                </NavigationProvider>
            </ScreenSizeProvider>
        </ErrorBoundary>
    );
};

export default App;
